import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from './ui/button';
import { AlertCircle, CreditCard, Check } from 'lucide-react';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#fff',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#71717a',
      },
    },
    invalid: {
      color: '#f87171',
      iconColor: '#f87171',
    },
  },
};

const StripeCardInput = ({ onCardSaved, clientSecret }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [succeeded, setSucceeded] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);

    // Confirm the SetupIntent
    const { error: confirmError, setupIntent } = await stripe.confirmCardSetup(
      clientSecret,
      {
        payment_method: {
          card: cardElement,
        },
      }
    );

    if (confirmError) {
      setError(confirmError.message);
      setProcessing(false);
      return;
    }

    // SetupIntent succeeded
    if (setupIntent.status === 'succeeded') {
      setSucceeded(true);
      setProcessing(false);
      onCardSaved(setupIntent.id, setupIntent.payment_method);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2 mb-2">
        <CreditCard className="w-5 h-5 text-lime-400" />
        <h4 className="text-lg font-semibold text-white">Card Information</h4>
      </div>

      {succeeded ? (
        <div className="bg-lime-500/10 border border-lime-500/30 rounded-lg p-4 flex items-center space-x-3">
          <Check className="w-6 h-6 text-lime-400 flex-shrink-0" />
          <p className="text-lime-400 font-medium">Card saved successfully!</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4 mb-4">
            <CardElement options={CARD_ELEMENT_OPTIONS} />
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 flex items-start space-x-2">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <Button
            type="submit"
            disabled={!stripe || processing || succeeded}
            className="w-full bg-lime-500 text-black hover:bg-lime-600 disabled:opacity-50"
          >
            {processing ? 'Saving Card...' : 'Save Card'}
          </Button>

          <p className="text-zinc-500 text-xs mt-2 text-center">
            Your card will only be charged if you miss an appointment without proper cancellation
          </p>
        </form>
      )}
    </div>
  );
};

export default StripeCardInput;
